import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionHeader from "../components/SectionHeader";
import ApartmentCard from "../components/ApartmentCard";
import ReviewCard from "../components/ReviewCard";
import Gallery from "../components/Gallery";
import FaqAccordion from "../components/FaqAccordion";
import EnquiryForm from "../components/EnquiryForm";
import { apartments, reviews, faq, business, images, wines } from "../data/site";
import { usePhotoOverrides } from "../lib/photos";

export default function Home() {
  const photos = usePhotoOverrides();

  // Hero: single first uploaded → override static; else fallback.
  const heroPhoto = photos.first("hero");
  const heroSrc = heroPhoto?.src || images.hero;
  const heroAlt = heroPhoto?.alt || "Vinohrad při západu slunce na jižní Moravě";

  // Family story portrait override. Wait for API to resolve before choosing
  // fallback – prevents brief flash of the old placeholder.
  const storyPhoto = photos.first("story");
  const storyReady = photos.loaded;
  const storySrc = storyPhoto?.src || (storyReady ? images.familyPortrait : null);
  const storyAlt = storyPhoto?.alt || "Rodinné vinařství";

  // Gallery: use admin photos if ANY exist, else fallback to static list.
  // Wait for admin data to load so we do not first render Unsplash then swap.
  const adminGallery = photos.gallery();
  const galleryReady = photos.loaded;
  const galleryItems = !galleryReady ? [] : (adminGallery.length > 0 ? adminGallery : images.gallery);

  return (
    <main id="main">
      <Seo path="/" />

      {/* HERO */}
      <section className="relative min-h-[92vh] md:min-h-screen flex items-end overflow-hidden" data-testid="hero">
        <img src={heroSrc} alt={heroAlt} className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/25 to-charcoal/80" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24 text-ivory w-full">
          <div className="text-[11px] md:text-xs tracking-[.3em] uppercase text-gold mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-gold/70" /> Vinařství Brychta • Charvátská Nová Ves
          </div>
          <h1 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            Zážitky u vína,<br />na které se nezapomíná
          </h1>
          <p className="mt-6 text-base md:text-lg text-ivory/85 max-w-2xl leading-relaxed">
            Ubytování ve třech apartmánech, degustace našich vín a klidná základna pro výlety po Lednicko-valtickém areálu.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/ubytovani" data-testid="hero-cta-primary" className="bg-burgundy hover:bg-burgundy-hover text-ivory px-7 py-3.5 tracking-wide inline-flex items-center gap-2">
              Prohlédnout ubytování <ArrowRight size={16} />
            </Link>
            <Link to="/kontakt?type=ubytovani#poptavka" data-testid="hero-cta-secondary" className="border border-ivory/50 text-ivory hover:bg-ivory hover:text-charcoal px-7 py-3.5 tracking-wide transition-colors">
              Ověřit volný termín
            </Link>
          </div>
          <div className="mt-10 text-xs md:text-sm text-ivory/70 flex flex-wrap gap-x-4 gap-y-1">
            <span>Rodinné vinařství</span><span className="text-gold">•</span>
            <span>Jižní Morava</span><span className="text-gold">•</span>
            <span>Ubytování</span><span className="text-gold">•</span>
            <span>Degustace</span><span className="text-gold">•</span>
            <span>Vinohrad</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeader
              eyebrow="Víno, klid a jižní Morava"
              title="Přijeďte nejen ochutnat. Přijeďte na chvíli zpomalit."
              kicker="Ve Vinařství Brychta propojujeme vlastní moravské víno, ubytování a osobní přístup. Připravujeme degustace a pobyty pro páry, rodiny, cyklisty, skupiny přátel i firmy, které chtějí poznat jižní Moravu přirozeně a bez turistického shonu."
            />
          </div>
          <div className="lg:col-span-5 grid sm:grid-cols-3 gap-4 self-end">
            {[
              { t: "Rodinná atmosféra", d: "Přijímáme vás jako hosty, ne jako čísla v\u00A0rezervaci." },
              { t: "Víno přímo od\u00A0vinaře", d: "Naše víno ochutnáte tam, kde vyrostlo." },
              { t: "Program podle vás", d: "Sestavíme pobyt i\u00A0program na míru." },
            ].map((v) => (
              <div key={v.t} className="bg-cream p-5 border-l-2 border-gold">
                <h3 className="font-serif text-lg">{v.t}</h3>
                <p className="text-sm text-charcoal/70 mt-1.5 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACCOMMODATION */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader
            eyebrow="Ubytování kousek od Lednice"
            title={"Apartmány pro klidný pobyt na\u00A0jižní Moravě."}
            kicker="Nabízíme ubytování ve 3&nbsp;apartmánech. Dostupnost konkrétního apartmánu potvrzujeme osobně."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {apartments.map((a) => {
              const override = photos.first("apartment", a.slug);
              return <ApartmentCard key={a.slug} apt={a} imageOverride={override?.src} waiting={!photos.loaded} />;
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/kontakt#poptavka" className="inline-flex items-center bg-burgundy hover:bg-burgundy-hover text-ivory px-8 py-3.5 tracking-wide">Poptat volný termín</Link>
          </div>
        </div>
      </section>

      {/* WINE TEASER */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative bg-cream aspect-[4/5]">
            {(() => {
              const p = photos.first("home", "wine-intro");
              return p ? <img src={p.src} alt={p.alt || "Naše vína"} className="absolute inset-0 w-full h-full object-cover" loading="lazy" /> : null;
            })()}
          </div>
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Naše vína" title="Naše vína si můžete odvézt i objednat"
              kicker="Prohlédněte si aktuální nabídku lahví z našeho rodinného vinařství. Objednávku po odeslání osobně potvrdíme." />
            <div className="mt-8 flex flex-wrap gap-3">
              {wines.slice(0, 5).map((w) => (
                <span key={w.slug} className="text-sm text-charcoal/75 border border-charcoal/15 px-3 py-1.5">{w.name}</span>
              ))}
            </div>
            <Link to="/vina" data-testid="wine-teaser-cta" className="mt-8 inline-flex items-center gap-2 bg-burgundy hover:bg-burgundy-hover text-ivory px-7 py-3.5 tracking-wide">
              Prohlédnout nabídku vín <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="bg-vineyard text-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="text-[11px] tracking-[.3em] uppercase text-gold mb-4">Pro koho vinařství otevíráme</div>
          <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight max-w-3xl">Blízko Lednice, uprostřed vinohradů</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              { t: "Vinařský víkend pro páry", d: "Dvě noci v apartmánu, soukromá degustace a čas na procházky.", key: "audience-couples" },
              { t: "Dovolená pro cyklisty", d: "Uzamčené odkládání až pro cca 14 kol, doporučení tras v okolí.", key: "audience-cyclists" },
              { t: "Rodiny a turisté", d: "Klidné apartmány blízko Lednice a Valtic. Bez turistického shonu.", key: "audience-families" },
            ].map((a) => {
              const p = photos.first("home", a.key);
              return (
                <div key={a.t} className="bg-charcoal/20 border border-ivory/10">
                  <div className="w-full aspect-[4/3] bg-cream/10">
                    {p && <img src={p.src} alt={p.alt || a.t} className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl">{a.t}</h3>
                    <p className="text-sm text-ivory/75 mt-2 leading-relaxed">{a.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAMILY STORY */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            {storySrc ? (
              <img src={storySrc} alt={storyAlt} className="w-full aspect-[3/4] object-cover" loading="lazy" data-testid="family-story-image" />
            ) : (
              <div className="w-full aspect-[3/4] bg-cream" data-testid="family-story-placeholder" aria-hidden="true" />
            )}
          </div>
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Naše rodina" title="Rodinné vinařství s kořeny v Charvátské Nové Vsi" />
            <div className="mt-6 prose-czech text-charcoal/85 max-w-2xl">
              <p className="dropcap">
                Vinařství Brychta vzniklo v roce 2022, ale navazuje na rodinnou práci ve
                vinohradech v Charvátské Nové Vsi. Naši dědové hospodařili na místních
                tratích Ritopeky. Na jejich práci navazujeme s úctou, ale vlastní cestou.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Jak to funguje" title="Od výběru k pobytu ve čtyřech krocích" />
          <div className="mt-14 grid md:grid-cols-4 gap-5">
            {["Vyberete si zážitek nebo ubytování.", "Odešlete nezávaznou poptávku.", "Potvrdíme dostupnost, cenu a podrobnosti.", "Po uhrazení zálohy je rezervace ubytování závazná."].map((s, i) => (
              <div key={i} className="bg-ivory p-6 border-l-2 border-burgundy">
                <div className="font-serif text-4xl text-burgundy/40">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 text-sm md:text-base text-charcoal/85 leading-relaxed">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="galerie" className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Galerie" title="Pohled do vinařství a okolí" />
          <div className="mt-10"><Gallery images={galleryItems} /></div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Recenze" title="Co říkají naši hosté"
            kicker={`Ubytování u vinaře hodnotí hosté na Googlu ${business.google.rating.toString().replace('.', ',')} z 5.`} />
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {reviews.map((r, i) => <ReviewCard key={i} review={r} />)}
          </div>
          <div className="mt-10 text-center">
            <a href={business.google.profileUrl} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-cta" className="inline-flex items-center gap-2 text-burgundy hover:text-burgundy-hover text-sm tracking-wide underline underline-offset-4">
              Zobrazit všech {business.google.ratingsCount} hodnocení <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Časté dotazy" title="Před vaším příjezdem" />
          </div>
          <div className="lg:col-span-8"><FaqAccordion items={faq} /></div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="poptavka" className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Poptávka" title="Řekněte nám, jaký pobyt nebo zážitek hledáte"
              kicker="Napište nám termín, počet osob a svou představu. Prověříme možnosti a ozveme se s konkrétní nabídkou." />
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex gap-3"><Phone size={18} className="text-burgundy mt-0.5" /><a href={`tel:${business.phoneHref}`} className="hover:text-burgundy">{business.phone}</a></div>
              <div>
                <div className="flex gap-3 items-start"><Mail size={18} className="text-burgundy mt-0.5" />
                  <div>
                    <div className="text-[11px] tracking-[.2em] uppercase text-charcoal/60">Vinařství, degustace</div>
                    <a href={`mailto:${business.emails.winery}`} className="hover:text-burgundy">{business.emails.winery}</a>
                  </div>
                </div>
                <div className="flex gap-3 items-start mt-3"><Mail size={18} className="text-burgundy mt-0.5" />
                  <div>
                    <div className="text-[11px] tracking-[.2em] uppercase text-charcoal/60">Ubytování</div>
                    <a href={`mailto:${business.emails.accommodation}`} className="hover:text-burgundy">{business.emails.accommodation}</a>
                  </div>
                </div>
              </div>
              <div className="flex gap-3"><MapPin size={18} className="text-burgundy mt-0.5" />
                <div>{business.address.street}<br />{business.address.postal} {business.address.city}</div>
              </div>
              <a href={business.google.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-burgundy hover:text-burgundy-hover text-sm underline underline-offset-4">
                Otevřít Google Mapy <ArrowRight size={14} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 bg-ivory p-6 md:p-10 border border-charcoal/10">
            <EnquiryForm />
          </div>
        </div>
      </section>
    </main>
  );
}
